import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { LocalizedMessage } from 'intl/LocalizedComponents';
import _get from 'lodash/get';
import PropTypes from 'prop-types';
// HOCs
import withFormManagement from 'hocs/WithFormManagement';
import withStateReinit from 'hocs/WithStateReinit';
// Core components
import Card from 'components/core/Card';
import TextField from 'components/core/TextField';
import Typography from 'components/core/Typography';
// Models
import mPartner from 'models/cpm/partner';
// Custom
import { cpmPartnersDataShape, errorShape } from 'constants/PropTypesShapes';
import {
  buildFormErrors,
  completeFormErrors,
  cpmPartnerFinancialInformationConstraints,
} from 'utils/Forms';
import FormDialogTemplate from 'components/templates/FormDialogTemplate';
import FormHelper from 'components/misc/FormHelper';
import CountryFieldSelector from 'components/inputFields/CountryFieldSelector';

class PartnerAddEditForm extends React.Component {
  constructor(props) {
    super(props);

    this.handleValidate = this.handleValidate.bind(this);
  }

  componentDidMount() {
    // We need to pass component reference to parent (= DialogItem)
    const { onRef } = this.props;
    onRef(this);
  }

  // Handlers called by parent (= DialogItem)
  handleValidate() {
    const {
      fields: {
        city,
        country,
        extra,
        street,
        zipCode,
        emailForCommissionInvoices,
        europeanVatNumber,
        iban,
        nationalIdentifier,
        name,
      },
      onErrorsChange,
      onValidate,
      partner,
    } = this.props;

    let errors;
    errors = buildFormErrors(
      {
        emailForCommissionInvoices,
        europeanVatNumber,
        iban,
        nationalIdentifier,
        street,
        city,
        zipCode,
        country,
      },
      cpmPartnerFinancialInformationConstraints,
    );

    if (!partner && !name) {
      errors = completeFormErrors(errors, 'name', ['cantBeBlank']);
    }

    // Apply errors
    onErrorsChange(errors);

    if (!errors) {
      let data;
      const financialInformation = {
        address: {
          city,
          country,
          extra,
          street,
          zipCode,
        },
        emailForCommissionInvoices,
        europeanVatNumber,
        iban,
        nationalIdentifier,
      };

      // If I have partner is edit else it's an add partner
      if (partner) {
        data = mPartner.serializeFinancialInformation(financialInformation);
      } else {
        data = mPartner.serialize({
          name,
          financialInformation,
        });
      }

      onValidate(data, partner);
    }
    return false;
  }

  render() {
    const {
      fields: {
        city,
        country,
        extra,
        street,
        zipCode,
        emailForCommissionInvoices,
        europeanVatNumber,
        iban,
        nationalIdentifier,
        name,
      },
      errors,
      onFieldsChange,
      partner,
    } = this.props;

    return (
      <FormDialogTemplate errors={errors}>
        <div className="row">
          <div className="col-12">
            <div className="d-flex">
              <FormHelper
                elements={[
                  {
                    title: <LocalizedMessage id="title.generalInfo" />,
                    content: '',
                  },
                ]}
              />
              <div style={{ flex: 1 }}>
                <Card
                  title={(
                    <Typography type="subtitle2">
                      <LocalizedMessage id="title.generalInfo" />
                    </Typography>
                  )}
                >
                  <div className="row">
                    {/* ----- Name ----- */}
                    {/* Show only for add partner */}
                    {!partner && (
                      <div className="col-12 col-lg-9">
                        <TextField
                          name="name"
                          required
                          label={<LocalizedMessage id="field.name" />}
                          errorText={_get(errors, 'name')}
                          value={name}
                          onChange={(value) => onFieldsChange({ name: value })}
                        />
                      </div>
                    )}
                    {/* ----- Street ----- */}
                    <div className="col-12 col-lg-9">
                      <TextField
                        name="street"
                        required
                        label={<LocalizedMessage id="field.address" />}
                        errorText={_get(errors, 'street')}
                        value={street}
                        onChange={(value) => onFieldsChange({ street: value })}
                      />
                    </div>
                    {/* ----- Extra ----- */}
                    <div className="col-12 col-lg-9">
                      <TextField
                        name="extra"
                        label={
                          <LocalizedMessage id="field.addressAdditional" />
                        }
                        value={extra}
                        onChange={(value) => onFieldsChange({ extra: value })}
                      />
                    </div>
                    {/* ----- zipCode ----- */}
                    <div className="col-12 col-lg-9">
                      <TextField
                        name="zipCode"
                        required
                        label={<LocalizedMessage id="field.zipcode" />}
                        errorText={_get(errors, 'zipCode')}
                        value={zipCode}
                        onChange={(value) => onFieldsChange({ zipCode: value })}
                      />
                    </div>
                    {/* ----- City ----- */}
                    <div className="col-12 col-lg-9">
                      <TextField
                        name="city"
                        required
                        label={<LocalizedMessage id="field.city" />}
                        errorText={_get(errors, 'city')}
                        value={city}
                        onChange={(value) => onFieldsChange({ city: value })}
                      />
                    </div>
                    {/* ----- Country ----- */}
                    <div className="col-12 col-lg-9">
                      <CountryFieldSelector
                        countriesScope="known"
                        value={country}
                        errorText={_get(errors, 'country')}
                        onChange={(value) => onFieldsChange({ country: value })}
                      />
                    </div>
                    {/* ----- emailForCommissionInvoices ----- */}
                    <div className="col-12 col-lg-9">
                      <TextField
                        required
                        label={
                          <LocalizedMessage id="field.emailForCommissionInvoices" />
                        }
                        errorText={_get(errors, 'emailForCommissionInvoices')}
                        value={emailForCommissionInvoices}
                        onChange={(value) => onFieldsChange({ emailForCommissionInvoices: value })}
                      />
                    </div>
                  </div>
                </Card>
              </div>
            </div>

            <div className="d-flex pt-3">
              <FormHelper
                elements={[
                  {
                    title: <LocalizedMessage id="title.financialInformation" />,
                    content: '',
                  },
                ]}
              />
              <div style={{ flex: 1 }}>
                <Card
                  title={(
                    <Typography type="subtitle2">
                      <LocalizedMessage id="title.financialInformation" />
                    </Typography>
                  )}
                >
                  <div className="row">
                    {/* ----- europeanVatNumber ----- */}
                    <div className="col-12 col-lg-9">
                      <TextField
                        required
                        label={
                          <LocalizedMessage id="field.europeanVatNumber" />
                        }
                        errorText={_get(
                          errors,
                          'financialInformation.europeanVatNumber',
                        )}
                        value={europeanVatNumber}
                        onChange={(value) => onFieldsChange({ europeanVatNumber: value })}
                      />
                    </div>
                    {/* ----- iban ----- */}
                    <div className="col-12 col-lg-9">
                      <TextField
                        required
                        label={<LocalizedMessage id="field.iban" />}
                        errorText={_get(errors, 'financialInformation.iban')}
                        value={iban}
                        onChange={(value) => onFieldsChange({ iban: value })}
                      />
                    </div>
                    {/* ----- nationalIdentifier ----- */}
                    <div className="col-12 col-lg-9">
                      <TextField
                        required
                        label={
                          <LocalizedMessage id="field.nationalIdentifier" />
                        }
                        errorText={_get(
                          errors,
                          'financialInformation.nationalIdentifier',
                        )}
                        value={nationalIdentifier}
                        onChange={(value) => onFieldsChange({ nationalIdentifier: value })}
                      />
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </FormDialogTemplate>
    );
  }
}

PartnerAddEditForm.propTypes = {
  partner: cpmPartnersDataShape,
  operationError: errorShape, // eslint-disable-line react/no-unused-prop-types
  fields: PropTypes.object.isRequired,
  errors: PropTypes.object,
  onFieldsChange: PropTypes.func.isRequired,
  onErrorsChange: PropTypes.func.isRequired,
  onRef: PropTypes.func.isRequired,
  onValidate: PropTypes.func.isRequired,
};

PartnerAddEditForm.defaultProps = {
  partner: null,
  operationError: null,
  errors: null,
};

const mapFieldsToProps = {
  name: {
    resourceKey: 'name',
    defaultValue: '',
  },
  city: {
    resourceKey: 'financialInformation.address.city',
    defaultValue: '',
  },
  country: {
    resourceKey: 'financialInformation.address.country',
    defaultValue: '',
  },
  extra: {
    resourceKey: 'financialInformation.address.extra',
    defaultValue: '',
  },
  street: {
    resourceKey: 'financialInformation.address.street',
    defaultValue: '',
  },
  zipCode: {
    resourceKey: 'financialInformation.address.zipCode',
    defaultValue: '',
  },
  emailForCommissionInvoices: {
    resourceKey: 'financialInformation.emailForCommissionInvoices',
    defaultValue: '',
  },
  europeanVatNumber: {
    resourceKey: 'financialInformation.europeanVatNumber',
    defaultValue: '',
  },
  iban: {
    resourceKey: 'financialInformation.iban',
    defaultValue: '',
  },
  nationalIdentifier: {
    resourceKey: 'financialInformation.nationalIdentifier',
    defaultValue: '',
  },
};

const mapOwnPropsToResources = (ownProps) => ({
  resourceData: _get(ownProps, 'partner'),
  operationError: {
    ...ownProps.updateFinancialInformationError,
    ...ownProps.cpmCreatePartnerError,
  },
});

const mapStateToProps = (state) => ({
  updateFinancialInformationError: _get(
    state.cpmPartner.updateFinancialInformation,
    'error',
  ),
  cpmCreatePartnerError: _get(state.cpmPartner.add, 'error'),
});

module.exports = compose(
  connect(mapStateToProps, null),
  withFormManagement(mapFieldsToProps, mapOwnPropsToResources, false),
  withStateReinit(['updatePartnerFinancialInformation', 'addPartner']),
)(PartnerAddEditForm);



// WEBPACK FOOTER //
// ./routes/cpm/routes/workspaces/assets/partner/PartnerAddEditForm.js